import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import List from '@mui/material/List';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import CardContent from '@mui/material/CardContent';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';
import { IOrgCandidate } from 'src/services/candidates/candidates.types';
import { useGetOrganizationCandidatesQuery } from 'src/services/candidates/candidates.service';
import { useCreatePresentationMutation } from 'src/services/presentations/presentations.service';
import {
  IDealCandidateLink,
  useGetDealCandidateLinksQuery,
  useAddDealCandidateLinksMutation,
  useRemoveDealCandidateLinkMutation,
} from 'src/services/deal-flow/deal-candidates.service';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import BulkPresentationsDialog from 'src/sections/presentations/modals/bulk-presentations-dialog';

import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

/** JobTetris's own anonymous template: no name, contacts or photo, employers described not named. */
export const MARKETING_TEMPLATE_KEY = 'anonymous_profile';

const PER_PAGE = 5;

type Props = {
  dealId: string;
  dealTitle?: string;
};

const nameOf = (candidate: IDealCandidateLink['candidate']) => [candidate.first_name, candidate.last_name].filter(Boolean).join(' ');

/**
 * The candidates an agency is putting forward on a deal (JT1-1887, C10). With the presentation
 * builder, each one can get an anonymised marketing profile for the deal's client, made from the
 * anonymous template and opened in the builder to check before it goes anywhere; or all of them at
 * once, zipped.
 */
export function DealCandidatesCard({ dealId, dealTitle }: Props) {
  const { t } = useTranslate();
  const router = useRouter();
  const { featureEnabled } = useFeatureFlags();
  const canPresent = featureEnabled(FeatureType.CANDIDATE_PRESENTATIONS_ENABLED);

  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; link: IDealCandidateLink } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<IDealCandidateLink | null>(null);

  const { currentData: links, isLoading } = useGetDealCandidateLinksQuery({ dealId, page, perPage: PER_PAGE });
  // Everyone on the deal, for marketing profiles for all of them (a bulk run takes up to 50).
  const { currentData: allLinks } = useGetDealCandidateLinksQuery({ dealId, page: 1, perPage: 50 }, { skip: !bulk });
  const [removeLink, { isLoading: isRemoving }] = useRemoveDealCandidateLinkMutation();
  const [createPresentation, { isLoading: isCreating }] = useCreatePresentationMutation();

  const marketingTitle = dealTitle ? t('presentations.deal_candidates.marketing_title', { deal: dealTitle }) : undefined;

  const createMarketingProfile = async (link: IDealCandidateLink) => {
    setMenu(null);
    try {
      const presentation = await createPresentation({ candidateId: link.candidate.id, template_key: MARKETING_TEMPLATE_KEY, anonymise: true, title: marketingTitle }).unwrap();
      router.push(paths.dashboard.candidates.presentation(link.candidate.id, presentation.id));
    } catch {
      enqueueSnackbar(t('presentations.deal_candidates.marketing_failed'), { variant: 'error' });
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    try {
      await removeLink({ dealId, linkId: confirmRemove.id }).unwrap();
      enqueueSnackbar(t('presentations.deal_candidates.removed'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.deal_candidates.action_failed'), { variant: 'error' });
    }
    setConfirmRemove(null);
  };

  const count = links?.count ?? 0;

  return (
    <Card>
      <CardHeader
        title={t('presentations.deal_candidates.title')}
        action={(
          <Button size="small" startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => setAdding(true)}>
            {t('presentations.deal_candidates.add')}
          </Button>
        )}
      />
      <CardContent sx={{ pt: 1 }}>
        {!isLoading && !count ? (
          <EmptyContent title={t('presentations.deal_candidates.empty')} sx={{ py: 3 }} />
        ) : (
          <List disablePadding>
            {(links?.results ?? []).map((link) => (
              <ListItem
                key={link.id}
                disableGutters
                secondaryAction={(
                  <IconButton size="small" aria-label={t('presentations.deal_candidates.more', { name: nameOf(link.candidate) })} onClick={(event) => setMenu({ anchor: event.currentTarget, link })}>
                    <Iconify icon="eva:more-vertical-fill" />
                  </IconButton>
                )}
              >
                <ListItemText
                  primary={(
                    <Typography component={RouterLink} href={paths.dashboard.candidates.detail(link.candidate.id)} variant="subtitle2" sx={{ color: 'text.primary', textDecoration: 'none' }}>
                      {nameOf(link.candidate) || t('presentations.deal_candidates.unnamed')}
                    </Typography>
                  )}
                  secondary={[link.candidate.position_title, link.note].filter(Boolean).join(' · ') || undefined}
                  secondaryTypographyProps={{ noWrap: true }}
                  sx={{ pr: 5 }}
                />
              </ListItem>
            ))}
          </List>
        )}

        {count > PER_PAGE && (
          <Stack alignItems="center" sx={{ mt: 1 }}>
            <Pagination size="small" count={Math.ceil(count / PER_PAGE)} page={page} onChange={(_event, next) => setPage(next)} />
          </Stack>
        )}

        {canPresent && count > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button fullWidth variant="soft" startIcon={<Iconify icon="solar:documents-bold" />} onClick={() => setBulk(true)}>
              {t('presentations.deal_candidates.marketing_all', { count })}
            </Button>
          </Box>
        )}
      </CardContent>

      <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {canPresent && (
          <MenuItem disabled={isCreating} onClick={() => menu && createMarketingProfile(menu.link)}>
            <Iconify icon="solar:document-add-bold" sx={{ mr: 1 }} />
            {t('presentations.deal_candidates.marketing_one')}
          </MenuItem>
        )}
        <MenuItem onClick={() => { setConfirmRemove(menu?.link ?? null); setMenu(null); }} sx={{ color: 'error.main' }}>
          <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 1 }} />
          {t('presentations.deal_candidates.remove')}
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title={t('presentations.deal_candidates.remove_title')}
        content={t('presentations.deal_candidates.remove_body', { name: confirmRemove ? nameOf(confirmRemove.candidate) : '' })}
        action={<LoadingButton variant="contained" color="error" loading={isRemoving} onClick={handleRemove}>{t('presentations.deal_candidates.remove')}</LoadingButton>}
      />

      {adding && <AddDealCandidatesDialog dealId={dealId} onClose={() => setAdding(false)} />}

      {bulk && allLinks && (
        <BulkPresentationsDialog
          open
          onClose={() => setBulk(false)}
          candidateIds={allLinks.results.map((link) => link.candidate.id)}
          defaultTemplateKey={MARKETING_TEMPLATE_KEY}
          defaultAnonymise
          defaultTitle={marketingTitle}
        />
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------

function AddDealCandidatesDialog({ dealId, onClose }: { dealId: string; onClose: VoidFunction }) {
  const { t } = useTranslate();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<IOrgCandidate[]>([]);
  const [note, setNote] = useState('');

  const { currentData: candidates, isFetching } = useGetOrganizationCandidatesQuery({ search_query: search, page: { page: 1, per_page: 10 } });
  const [addLinks, { isLoading }] = useAddDealCandidateLinksMutation();

  const onSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), []);

  const handleAdd = async () => {
    try {
      await addLinks({ dealId, organization_candidate_ids: selected.map((candidate) => candidate.id), note: note.trim() || undefined }).unwrap();
      enqueueSnackbar(t('presentations.deal_candidates.added', { count: selected.length }), { variant: 'success' });
      onClose();
    } catch {
      enqueueSnackbar(t('presentations.deal_candidates.action_failed'), { variant: 'error' });
    }
  };

  const label = (candidate: IOrgCandidate) => [candidate.candidate_profile?.first_name, candidate.candidate_profile?.last_name].filter(Boolean).join(' ');

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('presentations.deal_candidates.add_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Autocomplete
            multiple
            options={candidates?.results ?? []}
            value={selected}
            loading={isFetching}
            filterOptions={(options) => options}
            getOptionLabel={label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setSelected(value)}
            onInputChange={(_event, value) => onSearch(value)}
            renderInput={(params) => <TextField {...params} label={t('presentations.deal_candidates.search')} />}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('presentations.deal_candidates.note')}
            helperText={t('presentations.deal_candidates.note_helper')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!selected.length} onClick={handleAdd}>
          {t('presentations.deal_candidates.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
